import React from 'react';
import clsx from 'clsx';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link } from '@elevator/ui';
import useStyles from './styles';

/** MUI Typography variant strings for type safety */
type TypographyVariant = React.ComponentProps<typeof Typography>['variant'];

/**
 * Props for the PageHeader component.
 */
export interface PageHeaderProps {
  /** Optional breadcrumbs node (e.g. MUI Breadcrumbs) rendered above the page title. Not shown by default. */
  breadcrumbs?: React.ReactNode;
  /** Page title text. Ignored when `titleSlot` is set. */
  title?: string;
  /**
   * Replaces the title text (e.g. loading skeleton). Use instead of showing a temporary slug
   * before API data resolves. Rendered inside the same title Typography as plain `title`.
   */
  titleSlot?: React.ReactNode;
  /** When set, the title is rendered as a link to this URL (e.g. hub page). */
  titleHref?: string;
  /** Typography variant for the title (style only). Default: `h1`. */
  titleVariant?: TypographyVariant;
  /** HTML element or component for the title (semantics). */
  titleComponent?: React.ElementType;
  /** Optional action(s) in the title row (e.g. one or more Buttons). Pass multiple elements to show several actions. */
  actions?: React.ReactNode;
  /** Optional subheader content below the title row and above the divider, in a horizontal stack (pass multiple elements e.g. icon + typography). */
  subHeader?: React.ReactNode;
  /** When true, render a full-width Divider below the title block. Default: true. */
  divider?: boolean;
  /** Optional description, rendered below the divider. String: wrapped in Typography. ReactNode: rendered as-is (descriptionVariant/descriptionComponent ignored). */
  description?: React.ReactNode;
  /** Typography variant for the description when description is a string. Default: `body1`. */
  descriptionVariant?: TypographyVariant;
  /** HTML element or component for the description when description is a string. */
  descriptionComponent?: React.ElementType;
}

/**
 * PageHeader Component
 *
 * Renders a page-level header (semantic <header>) with optional breadcrumbs above
 * the title, optional title row (title + actions), optional subHeader below the title,
 * a divider, and optional description below the divider. Uses "Page" terminology
 * for semantics. Typography variants and components are configurable for
 * accessibility and styling.
 */
const PageHeader = ({
  breadcrumbs,
  title,
  titleSlot,
  titleHref,
  titleVariant = 'h1',
  titleComponent,
  actions,
  subHeader,
  divider = true,
  description,
  descriptionVariant = 'body1',
  descriptionComponent,
}: PageHeaderProps) => {
  const { classes } = useStyles();

  const titleTypography = (() => {
    if (titleSlot != null) {
      return (
        <Typography
          variant={titleVariant}
          component={titleComponent}
          className={classes.title}
          aria-busy="true"
        >
          {titleSlot}
        </Typography>
      );
    }
    if (titleHref != null && titleHref !== '') {
      return (
        <Link href={titleHref}>
          <Typography
            variant={titleVariant}
            component={titleComponent}
            className={clsx(classes.title, classes.titleAsLink)}
          >
            {title}
          </Typography>
        </Link>
      );
    }
    return (
      <Typography
        variant={titleVariant}
        component={titleComponent}
        className={classes.title}
      >
        {title}
      </Typography>
    );
  })();

  return (
    <Stack
      component="header"
      direction="column"
      spacing={1}
      className={classes.root}
      data-component="PageHeader"
    >
      {breadcrumbs != null && breadcrumbs}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        className={classes.titleStack}
      >
        {titleTypography}
        {actions != null && (
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
            {actions}
          </Stack>
        )}
      </Stack>
      {subHeader != null && (
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
          {subHeader}
        </Stack>
      )}
      {divider ? <Divider variant="fullWidth" /> : null}
      {description != null &&
        description !== '' &&
        (typeof description === 'string' ? (
          <Typography
            variant={descriptionVariant}
            component={descriptionComponent}
            className={classes.description}
          >
            {description}
          </Typography>
        ) : (
          <div className={classes.description}>{description}</div>
        ))}
    </Stack>
  );
};

export default PageHeader;
